import { CheckCircle } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import InfestationService from "../../api/InfestationService";
import MonitorService, {
  MonitorEventByIdResponse,
} from "../../api/MonitorService";
import PropertyService from "../../api/PropertyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomCollapseWithChildren } from "../../components/CustomCollapseWithChildren/CustomCollapseWithChildren";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { InfestationDetails } from "../../components/EventDetails/InfestationDetails";
import { MaintenanceDetails } from "../../components/EventDetails/MaintenanceDetails";
import { NoInfestationDetails } from "../../components/EventDetails/NoInfestationDetails";
import { UnreachableDetails } from "../../components/EventDetails/UnreachableDetails";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import FullScreenPreview from "../../components/FullScreenPreview/FullScreenPreview";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import PinCropMap from "../../components/PinCropMap/PinCropMap";
import { ServiceInfoBox } from "../../components/ServiceInfoBox/ServiceInfoBox";
import { useScreenSize } from "../../hooks/useScreenSize";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import styles from "../../styles/monitor-event-detail-view.module.css";
import { Document } from "../../types/document";
import { InfestationPest } from "../../types/infestation";
import { ServiceInfo } from "../Customer/Dashboard/ServiceInfo";

interface EventTypes {
  infestation: JSX.Element;
  noInfestation: JSX.Element;
  maintenancePerformed: JSX.Element;
  unreachableOrMissing: JSX.Element;
}

const typeMappings: { [key: string]: string } = {
  infestation: "Befall",
  noInfestation: "Kein Befall",
  maintenancePerformed: "Pflege",
  unreachableOrMissing: "Nicht erreichbar",
  notInspected: "Nicht kontrolliert",
};

const COLOR: { [key: string]: string } = {
  infestation: "red",
  noInfestation: "green",
  maintenancePerformed: "blue",
  unreachableOrMissing: "white",
  notInspected: "yellow",
};

export const MonitorEventDetailView = () => {
  const navigate = useNavigate();
  const { isDesktop } = useScreenSize();
  const { t } = useTranslation();
  const [event, setEvent] = useState<MonitorEventByIdResponse>();
  const [infestations, setInfestations] = useState<InfestationPest[]>();
  const [propertyAreaMap, setPropertyAreaMap] = useState<Document>();
  const { id } = useParams();
  const { state } = useLocation();
  const isViewOnly = useSelector((state: RootState) => state.property.viewOnly);

  const appointmentPropAreas = useSelector(
    (state: RootState) =>
      state.appointmentPropertyAreas.appointmentPropertyAreas
  );

  const eventTypes: EventTypes = {
    infestation: (
      <InfestationDetails event={event} infestations={infestations} />
    ),
    noInfestation: <NoInfestationDetails event={event} />,
    maintenancePerformed: <MaintenanceDetails event={event} />,
    unreachableOrMissing: <UnreachableDetails event={event} />,
  };

  useEffect(() => {
    if (id) {
      MonitorService.GetMonitorEventById(id)
        .then(async (event) => {
          setEvent(event);

          if (event.data.infestationId) {
            const response = await InfestationService.GetInfestationById(
              event.data.infestationId
            );
            setInfestations(response.data.pests);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [id, state?.monitor]);

  const propertyAreaId = event?.includes?.monitor[0].propertyAreaId;
  const monitorOriginalVersion = event?.data?.monitorVersions?.find(
    (version: any) => version.isOriginal === 1
  );
  const monitor = event?.includes?.monitor[0];

  useEffect(() => {
    if (!propertyAreaId) return;

    const matchedArea = appointmentPropAreas?.find(
      (area) => area.id === propertyAreaId
    );

    if (matchedArea?.map) {
      setPropertyAreaMap(matchedArea.map);
      return;
    }

    PropertyService.GetPropertyArea(propertyAreaId)
      .then((propertyArea: any) => {
        if (propertyArea?.data?.map) {
          setPropertyAreaMap(propertyArea.data.map);
        }
      })
      .catch((error) => console.error(error));
  }, [propertyAreaId, appointmentPropAreas, setPropertyAreaMap]);

  const handleBackButtonClicked = () => {
    goOneStepBack(navigate);
  };

  const renderEventDetails = () => {
    return event?.data?.eventType ? (
      eventTypes[event?.data?.eventType as keyof typeof eventTypes]
    ) : (
      <LoadingSpinner loading />
    );
  };

  const crop = {
    x: isViewOnly ? (monitor?.x ?? 1) : (monitorOriginalVersion?.x ?? 1),
    y: isViewOnly ? (monitor?.y ?? 1) : (monitorOriginalVersion?.y ?? 1),
  };

  const monitorPinAbbr = {
    name: event?.includes?.monitor[0]?.referenceNumber
      .slice(0, 3)
      .toUpperCase(),
    number: isViewOnly
      ? (monitor?.isTemporary ? "T" : "") + (monitor?.position ?? "001")
      : (monitor?.isTemporary ? "T" : "") +
        (monitorOriginalVersion?.position ?? "001"),
  };

  return (
    <AppLayout hideHeader>
      <BackButtonHeader
        handleBackButtonClicked={handleBackButtonClicked}
        showDocumentation={false}
      />
      <AppointmentDetails
        isServicesOverview
        lastEventDate={state.lastEventDate}
      />

      <div className={styles["content-wrapper"]}>
        <ServiceInfoBox
          data={state.monitor}
          typeOfService={typeMappings[event?.data?.eventType ?? ""] || ""}
          color={COLOR[event?.data?.eventType ?? ""] || "white"}
          hideCheck
          {...(isViewOnly && state.typeOfAppointment
            ? {
                lastServiceDate: event?.data?.changedAt,
                typeOfAppointment: state.typeOfAppointment,
                trapType: state.monitorTrapType,
              }
            : { trapType: event?.data.infestation })}
        />

        <div className={styles["map"]}>
          <CustomCollapseWithChildren
            active={!!propertyAreaMap?.url}
            collapsedByDefault
            rightIcon="collapseArrow"
            label={t("Map")}
            defaultText={t("NoMapAvailable")}
          >
            <div className={styles["map-container"]}>
              {propertyAreaMap && (
                <div className={styles["map-inner"]}>
                  <PinCropMap
                    map={propertyAreaMap}
                    crop={crop}
                    monitorPinAbbr={monitorPinAbbr}
                    onCropChange={() => {}}
                    isDraggable={false}
                    zoom={false}
                  />
                  <FullScreenPreview>
                    <PinCropMap
                      map={propertyAreaMap}
                      crop={crop}
                      onCropChange={() => {}}
                      monitorPinAbbr={monitorPinAbbr}
                    />
                  </FullScreenPreview>
                </div>
              )}
            </div>
          </CustomCollapseWithChildren>
        </div>

        {state.showStationary && monitor && (
          <>
            {monitor?.wirelessId && (
              <div className={styles["stationary-tab"]}>
                <p>Digital Monitor</p>
                <CheckCircle color="success" />
              </div>
            )}

            {monitor?.isTemporary && (
              <div className={styles["stationary-tab"]}>
                <p>Temporärer Monitor</p>
                <CheckCircle color="success" />
              </div>
            )}

            {monitor?.wirelessId && (
              <CustomInput
                label="Digitaler Monitor"
                placeholder="Kennnummer manuell eintragen"
                initialValue={monitor?.wirelessId}
                hideTick
                readonly
              />
            )}
          </>
        )}

        {renderEventDetails()}

        {state.lastServices && (
          <div className={styles["last-services"]}>
            <p className={styles["last-services__title"]}>
              Letzte Kundenbesuche
            </p>
            {Array.isArray(state.lastServices) &&
            state.lastServices.length > 0 ? (
              state.lastServices.map((service: any, index: number) => (
                <ServiceInfo
                  key={index}
                  service={service}
                  typeOfAppointment={
                    state.lastServicesIncludes[index]?.appointment
                      ?.typeOfAppointment
                  }
                  hideBorderBottom
                  roundedType
                />
              ))
            ) : (
              <p>{t("NoLastServices")}</p>
            )}
          </div>
        )}
      </div>
      <FloatingButtons>
        <CustomButton
          color="gray"
          {...(!isDesktop ? { icon: "leftarrow", iconPosition: "left" } : {})}
          width={70}
          onClick={handleBackButtonClicked}
        >
          {t("Back to overview")}
        </CustomButton>
      </FloatingButtons>
    </AppLayout>
  );
};
